import React from 'react';
import { Link } from 'react-router-dom';
const FoundationProgram = () => {
  const programDetails = {
    title: 'Foundation Programmes',
    overview: 'Our Foundation Programmes are designed to provide students with the essential academic skills and subject knowledge required for successful undergraduate study. These programs serve as a bridge between high school and university, ensuring students are well-prepared for the rigors of degree-level education.',
    duration: '1 Year (2 Semesters)',
    intake: 'January, May, September',
    entryRequirements: [
      'Minimum 5 O-Levels with grade C or above (including English and Mathematics)',
      'Equivalent international qualifications considered',
      'English language proficiency (IELTS 5.5 or equivalent)'
    ],
    programStructure: [
      {
        semester: 'Semester 1',
        modules: [
          'Academic English',
          'Mathematics for University',
          'Study Skills',
          'Introduction to Research Methods',
          'Foundation in Business (Elective)'
        ]
      },
      {
        semester: 'Semester 2',
        modules: [
          'Advanced Academic English',
          'Statistics',
          'Critical Thinking',
          'Information Technology',
          'Foundation in Science (Elective)'
        ]
      }
    ],
    careerProspects: [
      'Undergraduate degree programs in various fields',
      'Entry-level positions in business and administration',
      'Further professional qualifications'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{programDetails.title}</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Build a strong academic foundation for your undergraduate studies with our comprehensive foundation program.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Overview */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Program Overview</h2>
              <p className="text-gray-700 mb-6">{programDetails.overview}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">Duration</h3>
                  <p className="text-gray-700">{programDetails.duration}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">Intake Periods</h3>
                  <p className="text-gray-700">{programDetails.intake}</p>
                </div>
              </div>
            </section>

            {/* Program Structure */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Program Structure</h2>
              
              {programDetails.programStructure.map((semester, index) => (
                <div key={index} className="mb-8">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">{semester.semester}</h3>
                  <ul className="space-y-2">
                    {semester.modules.map((module, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span className="text-gray-700">{module}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>

            {/* Career Prospects */}
            <section className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Career Prospects</h2>
              <p className="text-gray-700 mb-4">Upon successful completion of the Foundation Programme, students can progress to various undergraduate degree programs or consider the following pathways:</p>
              <ul className="space-y-2">
                {programDetails.careerProspects.map((prospect, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{prospect}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Entry Requirements */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Entry Requirements</h2>
              <ul className="space-y-3">
                {programDetails.entryRequirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">✓</span>
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="mt-6 inline-block bg-blue-900 text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors w-full text-center"
              >
                Contact Admissions
              </Link>
            </div>

            {/* Quick Links */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Related Programs</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/academics/business-management" className="text-blue-700 hover:underline">
                    Business & Management
                  </Link>
                </li>
                <li>
                  <Link to="/academics/computing-it" className="text-blue-700 hover:underline">
                    Computing & IT
                  </Link>
                </li>
                <li>
                  <Link to="/academics/undergraduate" className="text-blue-700 hover:underline">
                    View All Undergraduate Programs
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Your Academic Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our Foundation Programme and take the first step towards your dream degree.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/admissions/apply-now"
              className="bg-white text-blue-900 px-8 py-3 rounded-md font-medium hover:bg-blue-100 transition-colors"
            >
              Apply Now
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-blue-800 transition-colors"
            >
              Request Information
            </Link>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default FoundationProgram;
